import { managerActions } from '@store/reducers/manager/actions'
import type { Channel } from 'redux-saga'
import {
  actionChannel,
  all,
  call,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects'

import { LibrarySagaActions } from '../actions'
import { ensureLibraryColors } from './ensureLibraryColors'
import { fetchLibrary } from './fetchLibrary'
import { invalidateLibrary } from './invalidateLibrary'
import { purgeMissingSymbols } from './purgeMissingSymbols'
import { removeLibrarySymbol } from './removeLibrarySymbol'
import { syncProjectLibrary } from './syncProjectLibrary'
import { updateLibrarySymbol } from './updateLibrarySymbol'

// Edits go one after another: each PATCH carries the version the previous one
// brought back, so quick successive edits never race each other into a 409.
function* watchUpdates() {
  const channel: Channel<ReturnType<typeof LibrarySagaActions.updateSymbol>> =
    yield actionChannel(LibrarySagaActions.updateSymbol.type)

  while (true) {
    const action: ReturnType<typeof LibrarySagaActions.updateSymbol> =
      yield take(channel)

    yield call(updateLibrarySymbol, action)
  }
}

// A freshly opened project: the own library list comes along (for those who
// may see it), the palette gets the library colors the refs need, and the refs
// whose items are gone (the server serves only what exists) leave.
function* onProjectLoaded() {
  yield call(fetchLibrary, LibrarySagaActions.fetch({ kind: 'symbol' }))
  yield call(ensureLibraryColors)
  yield call(purgeMissingSymbols)
}

export function* librarySaga() {
  yield all([
    takeLatest(LibrarySagaActions.fetch, fetchLibrary),
    takeEvery(LibrarySagaActions.invalidate, invalidateLibrary),
    takeEvery(LibrarySagaActions.removeSymbol, removeLibrarySymbol),
    takeLatest(managerActions.loadProject, onProjectLoaded),
    // A restored version may refer to items the current one did not — or to
    // items deleted since: re-read, then purge.
    takeLatest('PROJECT_RESTORE_SNAPSHOT', syncProjectLibrary),
    watchUpdates(),
  ])
}
